export const TIME_UNIT_MS = {
  month: 30 * 24 * 60 * 60 * 1000, // Approximate
  week: 7 * 24 * 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
  hour: 60 * 60 * 1000,
  minute: 60 * 1000,
  second: 1000,
} as const

export type TimeUnit = keyof typeof TIME_UNIT_MS

export type TimeUnits = {
  [K in TimeUnit]: number
}

/**
 * Converts a value and time unit to milliseconds
 */
export const toMilliseconds = (value: number, unit: TimeUnit): number => {
  return value * TIME_UNIT_MS[unit]
}

/**
 * Breaks down milliseconds into human readable time units for use in the UI
 */
export const fromMilliseconds = (ms: number): TimeUnits => {
  if (ms < 0) {
    throw new Error('fromMilliseconds expects a non-negative value')
  }

  let remaining = ms
  const result: TimeUnits = {
    month: 0,
    week: 0,
    day: 0,
    hour: 0,
    minute: 0,
    second: 0,
  }

  // Process in order from largest to smallest
  const units: TimeUnit[] = ['month', 'week', 'day', 'hour', 'minute', 'second']

  for (const unit of units) {
    result[unit] = Math.floor(remaining / TIME_UNIT_MS[unit])
    remaining = remaining % TIME_UNIT_MS[unit]
  }

  return result
}

/**
 * Finds the most appropriate time unit to represent the given milliseconds.
 * Returns the largest unit from allowedUnits that divides evenly into the value.
 * If no unit divides evenly, returns the closest value using the smallest allowed unit, rounded down.
 *
 * @param ms - The number of milliseconds to convert
 * @param allowedUnits - Array of time units to consider, ordered from largest to smallest (e.g., ['month', 'week', 'day', 'hour'])
 * @returns Object containing the numeric value and the selected time unit
 */
export const findOptimalTimeUnit = <T extends TimeUnit>(ms: number, allowedUnits: readonly T[]): { value: number; unit: T } => {
  if (ms < 0 || allowedUnits.length === 0) {
    throw new Error('findOptimalTimeUnit received invalid input(s)', { cause: { ms, allowedUnits } })
  }

  for (const unit of allowedUnits) {
    if (ms % TIME_UNIT_MS[unit] === 0) {
      return { value: ms / TIME_UNIT_MS[unit], unit }
    }
  }

  const fallbackUnit = allowedUnits[allowedUnits.length - 1] as T
  return { value: Math.floor(ms / TIME_UNIT_MS[fallbackUnit]), unit: fallbackUnit }
}
